import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

interface RollingNumberProps {
  value: number;
  className?: string;
  duration?: number;
  formatAsCurrency?: boolean;
  skipAnimation?: boolean;
}

const RollingNumber: React.FC<RollingNumberProps> = ({
  value,
  className = "",
  duration = 1000,
  formatAsCurrency = true,
  skipAnimation = false,
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (skipAnimation) {
      setDisplayValue(value);
      setIsAnimating(false);
      return;
    }

    if (displayValue === value) return;

    setIsAnimating(true);
    const startValue = displayValue;
    const endValue = value;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(
        startValue + (endValue - startValue) * eased
      );

      setDisplayValue(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration, skipAnimation]);

  const formattedValue = formatAsCurrency
    ? `$${displayValue.toLocaleString()}`
    : displayValue.toLocaleString();

  return (
    <motion.span
      className={`inline-block ${className}`}
      animate={{
        opacity: isAnimating ? [1, 0.9, 1] : 1,
      }}
      transition={{ duration: 0.1, repeat: isAnimating ? Infinity : 0 }}
      style={{
        textShadow: isAnimating
          ? "0 0 6px currentColor, 0 2px 2px rgba(0,0,0,1)"
          : "0 2px 2px rgba(0,0,0,1)",
        fontVariantNumeric: "tabular-nums", // Ensures consistent digit width
      }}
    >
      {formattedValue}
    </motion.span>
  );
};

export default RollingNumber;
